/**
 * Shared WebSocket chunking and decompression utilities
 * Handles reassembly of chunked compressed messages without external dependencies
 */

import { useEffect, useMemo, useRef, useState } from "react";

export interface ChunkedMessage {
  messageId: string;
  index: number;
  total: number;
  data: string; // base64 encoded chunk
}

interface MessageBuffer {
  chunks: Map<number, string>; // index -> base64 data
  total: number;
  receivedAt: number;
}

export function isChunkedMessage(message: unknown): message is ChunkedMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "messageId" in message &&
    "index" in message &&
    "total" in message &&
    "data" in message
  );
}

/**
 * Decompresses gzip-compressed data using browser's native DecompressionStream API
 * Returns null if API is not available
 */
async function decompressGzip(
  compressedData: Uint8Array
): Promise<string | null> {
  // Check if DecompressionStream is available
  if (typeof DecompressionStream === "undefined") {
    console.log(
      "DecompressionStream API not available in this browser. Chunked compressed messages are not supported."
    );
    return null;
  }

  const stream = new Blob([compressedData] as BlobPart[])
    .stream()
    .pipeThrough(new DecompressionStream("gzip"));

  const decompressedBlob = await new Response(stream).blob();
  return decompressedBlob.text();
}

/**
 * Converts base64 string to Uint8Array
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Creates a message buffer manager for handling chunked WebSocket messages
 */
export function createChunkedMessageHandler<T>() {
  const messageBuffers = new Map<string, MessageBuffer>();
  let cleanupInterval: number | NodeJS.Timeout | null = null;

  /**
   * Start automatic cleanup of stale message buffers
   */
  function startCleanup(intervalMs = 10000, maxAgeMs = 30000) {
    if (cleanupInterval !== null) return;

    cleanupInterval = setInterval(() => {
      const now = Date.now();
      for (const [messageId, buffer] of messageBuffers.entries()) {
        if (now - buffer.receivedAt > maxAgeMs) {
          messageBuffers.delete(messageId);
          console.warn(`Cleaned up incomplete message: ${messageId}`);
        }
      }
    }, intervalMs);
  }

  /**
   * Stop automatic cleanup
   */
  function stopCleanup() {
    if (cleanupInterval !== null) {
      clearInterval(cleanupInterval as number);
      cleanupInterval = null;
    }
  }

  /**
   * Process a potentially chunked message
   * Returns the reassembled message if complete, null if waiting for more chunks
   */
  async function processMessage(
    message: T | ChunkedMessage
  ): Promise<T | null> {
    // Check if this is a chunked message
    if (!isChunkedMessage(message)) {
      // Regular message, pass through
      return message;
    }

    const chunked = message;

    // Get or create buffer for this message
    let buffer = messageBuffers.get(chunked.messageId);
    if (!buffer) {
      buffer = {
        chunks: new Map(),
        total: chunked.total,
        receivedAt: Date.now(),
      };
      messageBuffers.set(chunked.messageId, buffer);
    } else {
      buffer.receivedAt = Date.now();
    }

    // Store this chunk
    buffer.chunks.set(chunked.index, chunked.data);

    // Check if we have all chunks
    if (buffer.chunks.size === buffer.total) {
      try {
        // Reassemble chunks in order
        const base64Chunks: string[] = [];
        for (let i = 0; i < buffer.total; i++) {
          const chunk = buffer.chunks.get(i);
          if (!chunk) {
            throw new Error(
              `Missing chunk ${i} for message ${chunked.messageId}`
            );
          }
          base64Chunks.push(chunk);
        }

        // Decode each base64 chunk individually and concatenate the binary data
        const chunks: Uint8Array[] = base64Chunks.map((chunk) =>
          base64ToUint8Array(chunk)
        );
        const totalLength = chunks.reduce(
          (sum, chunk) => sum + chunk.length,
          0
        );
        const compressedData = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
          compressedData.set(chunk, offset);
          offset += chunk.length;
        }

        // Decompress
        const decompressed = await decompressGzip(compressedData);

        // If decompression failed (API not available), clean up and return null
        if (decompressed === null) {
          messageBuffers.delete(chunked.messageId);
          return null;
        }

        // Parse JSON
        const parsedMessage = JSON.parse(decompressed) as T;

        // Clean up buffer
        messageBuffers.delete(chunked.messageId);

        return parsedMessage;
      } catch (error) {
        console.error("Failed to reassemble chunked message:", error);
        messageBuffers.delete(chunked.messageId);
        throw error;
      }
    }

    // Not all chunks received yet, return null to indicate waiting
    return null;
  }

  return {
    processMessage,
    startCleanup,
    stopCleanup,
    getBufferCount: () => messageBuffers.size,
  };
}

/**
 * Creates a shared WebSocket hook wrapper that handles chunked messages
 * This can be used with any WebSocket library that follows the react-use-websocket pattern
 *
 * @param useWebSocketHook - The base WebSocket hook (e.g., react-use-websocket's useWebSocket)
 * @template T - The type of the decoded WebSocket message
 * @template THookReturn - The return type of the base WebSocket hook
 * @template TOptions - The type of options accepted by the base WebSocket hook
 */
export function createChunkedWebSocketHook<T, THookReturn, TOptions = any>(
  useWebSocketHook: (
    url: string | null,
    options: TOptions
  ) => THookReturn & { lastJsonMessage?: unknown }
) {
  return (
    url: string | null,
    options?: Partial<TOptions>
  ): THookReturn & { lastJsonMessage: T | null } => {
    const [lastJsonMessage, setLastJsonMessage] = useState<T | null>(null);
    const handlerRef = useRef(createChunkedMessageHandler<T>());

    // Setup and cleanup message buffer handler
    useEffect(() => {
      const handler = handlerRef.current;
      handler.startCleanup();
      return () => handler.stopCleanup();
    }, []);

    const handlers = useMemo(() => {
      const handleMessage = async (message: T | ChunkedMessage) => {
        try {
          const result = await handlerRef.current.processMessage(message);
          if (result !== null) {
            setLastJsonMessage(result);
          }
        } catch (error) {
          console.error("Failed to process WebSocket message:", error);
        }
      };

      return {
        onError: (err: WebSocketEventMap["error"]) => {
          console.log(err);
        },
        onMessage: (event: MessageEvent) => {
          try {
            const message = JSON.parse(event.data);
            void handleMessage(message);
          } catch (error) {
            console.error("Failed to parse WebSocket message:", error);
          }
        },
        shouldReconnect: () => true,
      };
    }, [handlerRef, setLastJsonMessage]);

    const websocket = useWebSocketHook(url, {
      share: true,
      ...handlers,
      ...options,
    } as unknown as TOptions);

    return useMemo(
      () => ({
        ...websocket,
        lastJsonMessage,
      }),
      [websocket, lastJsonMessage]
    );
  };
}
